import { openDB } from 'idb';
let gameDB = null;
const GAME_DB_KEY = 'current';
const initGameDB = async () => {
    if (!gameDB) {
        gameDB = await openDB('gameDB', 1, {
            upgrade(db) {
                if (!db.objectStoreNames.contains('game')) {
                    db.createObjectStore('game');
                }
                if (!db.objectStoreNames.contains('gameTasks')) {
                    db.createObjectStore('gameTasks');
                }
                if (!db.objectStoreNames.contains('rallyInfo')) {
                    db.createObjectStore('rallyInfo');
                }
            },
        });
    }
    return gameDB;
};
export const GAME_DB = {
    SET: {
        async game(game) {
            const db = await initGameDB();
            await db.put('game', game, GAME_DB_KEY);
        },
        async gameTasks(gameTasks) {
            const db = await initGameDB();
            await db.put('gameTasks', gameTasks, GAME_DB_KEY);
        },
        async rallyInfo(rallyInfo) {
            const db = await initGameDB();
            await db.put('rallyInfo', rallyInfo, GAME_DB_KEY);
        },
        async gameTimeLeft(timeLeft) {
            const db = await initGameDB();
            await db.put('gameTimeLeft', timeLeft, GAME_DB_KEY);
        },
        async gameStatus(status) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.gameStatus = status;
            await db.put('game', game, GAME_DB_KEY);
        },
    },
    GET: {
        async game() {
            const db = await initGameDB();
            return (await db.get('game', GAME_DB_KEY)) ?? null;
        },
        async gameTasks() {
            const db = await initGameDB();
            return (await db.get('gameTasks', GAME_DB_KEY)) ?? null;
        },
        async rallyInfo() {
            const db = await initGameDB();
            return (await db.get('rallyInfo', GAME_DB_KEY)) ?? null;
        },
        async localAnswersTimeLogsList() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return null;
            }
            return game.localAnswersTimeLogs;
        },
        async gameStatus() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return null;
            }
            return game.gameStatus;
        },
        async localTaskAnswers() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return null;
            }
            return game.localTaskAnswers;
        },
        async taskUploadReference(taskId) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return null;
            }
            return game.uploads.find((item) => item.taskId === taskId)?.mediaReference || null;
        },
    },
    UPDATE: {
        async gameState(updatedFields) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            await db.put('game', {
                ...game,
                ...updatedFields,
            }, GAME_DB_KEY);
        },
        async taskResult(data) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            const { taskId: uid, status, pointsEarned, newAnswer } = data;
            const index = game.taskResults.findIndex((task) => task.taskId === uid);
            if (index === -1) {
                return;
            }
            game.taskResults[index] = {
                ...game.taskResults[index],
                status,
                totalPoints: game.taskResults[index].totalPoints + pointsEarned,
                playerAnswers: [...game.taskResults[index].playerAnswers, newAnswer],
            };
            game.totalPoints += pointsEarned;
            await db.put('game', game, GAME_DB_KEY);
        },
        async taskStatus(data) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            const { uid, status } = data;
            const index = game.taskResults.findIndex((task) => task.taskId === uid);
            if (index === -1) {
                return;
            }
            game.taskResults[index].status = status;
            await db.put('game', game, GAME_DB_KEY);
        },
        async addTaskAnswer(data) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            const { uid, newAnswer } = data;
            const index = game.taskResults.findIndex((task) => task.taskId === uid);
            if (index === -1) {
                return;
            }
            game.taskResults[index] = {
                ...game.taskResults[index],
                playerAnswers: [...game.taskResults[index].playerAnswers, newAnswer],
            };
            await db.put('game', game, GAME_DB_KEY);
        },
        async pushLocalAnswer(answer) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.localTaskAnswers.push(answer);
            await db.put('game', game, GAME_DB_KEY);
        },
        async clearLocalAnswers() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.localTaskAnswers = [];
            await db.put('game', game, GAME_DB_KEY);
        },
        async pushAnswerTimeLogs(currentTaskLogs) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.localAnswersTimeLogs.push(currentTaskLogs);
            await db.put('game', game, GAME_DB_KEY);
        },
        async clearLocalAnswersTimeLogs() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.localAnswersTimeLogs = [];
            await db.put('game', game, GAME_DB_KEY);
        },
        async pushTaskUploadItem(item) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.uploads.push(item);
            await db.put('game', game, GAME_DB_KEY);
        },
    },
    RESET: {
        async game() {
            const db = await initGameDB();
            await db.clear('rallyInfo');
            await db.clear('game');
            await db.clear('gameTasks');
        },
    },
};
